/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { FullAssignment, Nullable, ServiceLocation, WorkOrder } from "../../utils/Interfaces";
import React from "react";
import { Box, CircularProgress, Link, Paper, Stack, Tooltip, Typography } from "@mui/material";
import PanToolIcon from "@mui/icons-material/PanTool";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { customScrollbarStyle, getGoogleMapsURL } from "../../utils/Functions";
import { NavigateFunction, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import DirectionsIcon from "@mui/icons-material/Directions";
import { useAssignmentsForUser, useEditAssignmentPriority } from "./assignmentsHooks";

export interface AssigneeTicketListProps {
    userId: Nullable<number>;
}

const AssigneeTicketList = (props: AssigneeTicketListProps) => {
    const { userId } = props;

    const navigate: NavigateFunction = useNavigate();

    const { data, status } = useAssignmentsForUser(userId);
    const { mutate } = useEditAssignmentPriority(userId ?? 0);

    function reorder(list: FullAssignment[], startIndex: number, endIndex: number) {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    }

    const grid = 8;

    function onDragEnd(result: any) {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const items: FullAssignment[] = reorder(data?.results ?? [], result.source.index, result.destination.index);

        // If the DND did not result in a change, do not make a request.
        let originalIds: number[] = (data?.results ?? []).map<number>((assignment: FullAssignment) => assignment.id);
        let newIds: number[] = items.map<number>((assignment: FullAssignment) => assignment.id);

        if (originalIds.map<boolean>((id: number, index: number) => id === newIds[index]).every((result) => result)) {
            return;
        }

        mutate(items);
    }

    if (status === "pending") {
        return (
            <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
                <CircularProgress size={40} sx={{ mt: 20 }} />
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                maxHeight: "calc(100vh - 150px)",
                overflowY: "auto",
                overflowX: "hidden",
                ...customScrollbarStyle(),
            }}
        >
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided: any) => (
                        <Box {...provided.droppableProps} ref={provided.innerRef} style={{ padding: grid }}>
                            {data?.results.map((assignment: FullAssignment, index: number) => {
                                const { id, technician_entry } = assignment;

                                let work_order: WorkOrder = technician_entry.work_order;
                                let site: ServiceLocation = work_order.site;

                                return (
                                    <Draggable key={technician_entry.id} draggableId={id.toString()} index={index}>
                                        {(provided: any) => (
                                            <Paper
                                                sx={{ p: 4, width: "100%", mb: 1 }}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Stack direction="row" justifyContent="flex-start">
                                                        <Link
                                                            onClick={() => navigate(`/work-orders/${work_order.id}`)}
                                                            sx={{ cursor: "pointer" }}
                                                        >
                                                            <Typography variant="h6">
                                                                <b>{`${site.store_no}`}</b>
                                                                {` ${site.city}, ${site.state} WO ${work_order.ticket_no}`}
                                                            </Typography>
                                                        </Link>
                                                        <Tooltip title="Get Directions using Google Maps">
                                                            <IconButton
                                                                sx={{ ml: 1, mt: -0.25 }}
                                                                color="info"
                                                                onClick={() => window.open(getGoogleMapsURL(site, "_blank"))}
                                                            >
                                                                <DirectionsIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>

                                                    <PanToolIcon />
                                                </Stack>

                                                <Box>
                                                    <Typography variant="subtitle2">{work_order.problem_description}</Typography>
                                                </Box>
                                            </Paper>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </Box>
    );
};

export default AssigneeTicketList;
